import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { BalanceCountUp } from '@/lib/motion/BalanceCountUp.js';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover.js';
import type { WalletSnapshot } from '@/lib/wallet-snapshot.js';
import { currencyGlyph, formatMoney } from '@/lib/money/index.js';
import { rateLabel } from '@/lib/money/display-rate.js';
import { useDisplayMoney, useFormatMoney } from '@/components/money/MoneyProvider.js';

const POOL_META = [
  { key: 'creditCash'       as const, variant: 'cash',       color: 'var(--pool-cash)' },
  { key: 'creditPromotion'  as const, variant: 'bonus',      color: 'var(--pool-bonus)' },
  { key: 'commissionWallet' as const, variant: 'commission', color: 'var(--pool-commission)' },
  { key: 'cashbackWallet'   as const, variant: 'cashback',   color: 'var(--pool-cashback)' },
];

interface BalancePillProps {
  wallet: WalletSnapshot;
  onClick?: () => void;
}

export function BalancePill({ wallet, onClick }: BalancePillProps) {
  const { t } = useTranslation(['common', 'wallet']);

  const money = useDisplayMoney();
  const formatDisplay = useFormatMoney();
  // Only when a rate is actually in force. `moneyView` returns rate === null
  // both when the two currencies match and when no rate was quoted, and in the
  // second case `format` already falls back to the ledger figure — so a second
  // line would repeat the first.
  const converted = money.rate !== null && money.currency !== money.ledgerCurrency;
  // The real money leads everywhere in this menu and the conversion trails it,
  // so the figure a player acts on is never the approximated one.
  const formatHeld = (m: bigint | number) => formatMoney(m, money.ledgerCurrency);
  const heldExact = formatHeld(wallet.balance);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          onClick={() => onClick?.()}
          className="group inline-flex items-center gap-2.5 h-10 pl-1.5 pr-3.5 rounded-full bg-[var(--bg-elevated)] border border-[var(--border-strong)] hover:border-[var(--primary)] transition-colors"
        >
          <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[var(--primary)] to-[color-mix(in_oklab,var(--primary)_70%,#000)] text-[var(--primary-on)] grid place-items-center font-bold text-[13px] leading-none">
            {currencyGlyph(money.ledgerCurrency)}
          </span>
          <span className="text-sm font-bold bf-tnum text-[var(--text-primary)]">
            <BalanceCountUp value={wallet.balance} format={formatHeld} />
          </span>
          <ChevronDown
            className="size-3.5 text-[var(--text-muted)] transition-transform group-data-[state=open]:rotate-180"
            aria-hidden
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-0 rounded-xl border border-[var(--border-strong)] bg-[var(--bg-raised)] shadow-[var(--shadow-modal)] overflow-hidden"
      >
        <div className="px-4 py-3 border-b border-[var(--border-subtle)]">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)] mb-0.5">
            {t('common.totalBalance')}
          </div>
          <div className="text-2xl font-bold bf-tnum">{heldExact}</div>
          {converted && (
            <div className="mt-1 text-[11px] leading-tight text-[var(--text-muted)]">
              <span className="bf-tnum">{formatDisplay(wallet.balance)}</span>
              <span aria-hidden> · </span>
              <span className="bf-tnum">
                {rateLabel(money.rate!, money.currency, money.ledgerCurrency)}
              </span>
            </div>
          )}
        </div>
        <div className="p-2">
          {POOL_META.map((p) => (
            <div
              key={p.key}
              className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-[var(--bg-elevated)]/50"
            >
              <div className="flex items-center gap-2.5">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: p.color }}
                />
                <span className="text-[13px] text-[var(--text-secondary)]">{t(`pools.${p.variant}`, { ns: 'wallet' })}</span>
              </div>
              <span className="text-right">
                <span className="block text-[13px] font-semibold bf-tnum text-[var(--text-primary)]">
                  {formatMoney(wallet[p.key], money.ledgerCurrency)}
                </span>
                {converted && (
                  <span className="block text-[11px] leading-tight bf-tnum text-[var(--text-muted)]">
                    {formatDisplay(wallet[p.key])}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
